import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { Language, translations, Translations } from '../i18n';

const STORAGE_KEY = 'conductor_language';
const DEFAULT_LANGUAGE: Language = 'zh';

/**
 * Context value provided by LanguageProvider.
 */
interface LanguageContextValue {
  /** Current active language code. */
  language: Language;
  /** Switch the active language (persists to localStorage). */
  setLanguage: (lang: Language) => void;
  /**
   * Translate a dot-path key, with optional interpolation.
   * Falls back to English if key is missing in current language,
   * then falls back to the raw key if missing in both.
   *
   * @example t('login.title')                       // "Conductor"
   * @example t('chat_container.members_count', { count: 5 })  // "Members (5)"
   */
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

/**
 * Reads the initial language from localStorage, defaulting to 'zh'.
 */
function getInitialLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'zh') {
      return stored;
    }
  } catch {
    // localStorage unavailable (SSR, private browsing, etc.)
  }
  return DEFAULT_LANGUAGE;
}

/**
 * Resolves a dot-path key against a nested translation object.
 * Returns undefined if any segment is missing.
 */
function resolve(obj: Translations, key: string): string | undefined {
  const parts = key.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === 'string' ? current : undefined;
}

/**
 * Replaces `{variable}` placeholders in a string with provided values.
 */
function interpolate(template: string, params: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, name) => {
    return name in params ? String(params[name]) : match;
  });
}

/**
 * LanguageProvider wraps children with internationalization support.
 * Persists the selected language in localStorage under 'conductor_language'.
 */
export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // localStorage unavailable
    }
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      // Try current language first
      let value = resolve(translations[language], key);
      // Fall back to English
      if (value === undefined && language !== 'en') {
        value = resolve(translations.en, key);
      }
      // Fall back to raw key
      if (value === undefined) {
        return key;
      }
      // Interpolate if params provided
      if (params) {
        return interpolate(value, params);
      }
      return value;
    },
    [language]
  );

  const value = useMemo(
    () => ({ language, setLanguage, t }),
    [language, setLanguage, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

/**
 * Hook to access translation functions and language state.
 * Must be used within a LanguageProvider.
 */
export const useTranslation = (): LanguageContextValue => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
